import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import StatusChip from 'src/sections/@dashboard/marketing/components/StatusChip';
import { MKT_ADS_DESIGN as d } from './MktAds.design';
import {
    adResult,
    resultLabel,
    costPerResult,
    frequency,
    hasDelivery,
    budgetInfo,
    emptyReason,
    contextLine,
    creationDate,
    statusInfo,
    formatCurrency,
    formatDate,
    formatInt,
    formatPercent,
    formatDecimal
} from './constants';

// As duas ultimas colunas de metrica mudam de significado por nivel: alcance
// importa em campanha/conjunto (quantas pessoas), impressoes importa no
// anuncio (quantas vezes o criativo apareceu), e frequencia so faz sentido no
// conjunto, onde a fadiga de publico e acionavel.
const COLUMN_SPEC = {
    campaign: { m5: 'reach', m6: 'ctr' },
    adset: { m5: 'reach', m6: 'frequency' },
    ad: { m5: 'impressions', m6: 'ctr' }
};

// Badge de nivel por linha — necessario na arvore, onde niveis se misturam.
const LEVEL_BADGE_TONE = { campaign: 'brand', adset: 'info', ad: 'pink' };

function metricValue(kind, ad, t) {
    const insights = ad.insights || {};
    if (kind === 'reach') return formatInt(insights.reach);
    if (kind === 'impressions') return formatInt(insights.impressions);
    if (kind === 'ctr') return formatPercent(insights.ctr);
    if (kind === 'frequency') {
        const f = frequency(insights);
        return f === null ? '—' : formatDecimal(f, 1);
    }
    return '—';
}

export default function MktAdsTable({ ads, currency = 'BRL', level, structureIndex, childLists, onOpen, onDrill }) {
    const { t } = useTranslation('marketing');
    const spec = COLUMN_SPEC[level] || COLUMN_SPEC.campaign;
    const [expanded, setExpanded] = useState(() => new Set());

    const toggle = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // Indices de filhos por pai, pra montar a arvore sem ida ao servidor.
    const { adsetsByCampaign, adsByAdset } = useMemo(() => {
        const byCampaign = new Map();
        const byAdset = new Map();
        for (const s of childLists?.adsets || []) {
            const key = String(s.parent_ids?.campaign_id || '');
            if (!key) continue;
            if (!byCampaign.has(key)) byCampaign.set(key, []);
            byCampaign.get(key).push(s);
        }
        for (const a of childLists?.ads || []) {
            const key = String(a.parent_ids?.adset_id || '');
            if (!key) continue;
            if (!byAdset.has(key)) byAdset.set(key, []);
            byAdset.get(key).push(a);
        }
        return { adsetsByCampaign: byCampaign, adsByAdset: byAdset };
    }, [childLists]);

    const childrenOf = (ad) => {
        if (ad.level === 'campaign') return adsetsByCampaign.get(String(ad.external_id)) || [];
        if (ad.level === 'adset') return adsByAdset.get(String(ad.external_id)) || [];
        return [];
    };

    // Arvore achatada: cada entrada carrega a profundidade pra indentacao.
    // So a aba de anuncios e plana (anuncio nao tem filho).
    const flat = useMemo(() => {
        const out = [];
        const push = (ad, depth) => {
            out.push({ ad, depth });
            if (!expanded.has(ad._id)) return;
            for (const child of childrenOf(ad)) push(child, depth + 1);
        };
        for (const ad of ads) push(ad, 0);
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ads, expanded, adsetsByCampaign, adsByAdset]);

    const rows = useMemo(() => flat.map(({ ad, depth }) => {
        const result = adResult(ad);
        const delivered = hasDelivery(ad);
        return {
            ad,
            depth,
            result,
            delivered,
            cpr: costPerResult(ad, result),
            budget: budgetInfo(ad, currency, t),
            status: statusInfo(ad.effective_status || ad.status, t),
            created: creationDate(ad),
            // Dentro da arvore o pai esta visivel logo acima — o contexto
            // "em {pai}" so faz sentido na linha de topo.
            context: depth === 0 && ad.level !== 'campaign' ? contextLine(ad, structureIndex, t) : '',
            counts: structureIndex.trusted ? structureIndex.structureOf(ad) : null,
            reason: delivered
                ? null
                : emptyReason(ad, structureIndex.trusted ? structureIndex.structureOf(ad) : null, t)
        };
    }), [flat, currency, structureIndex, t]);

    const topRows = useMemo(() => rows.filter((row) => row.depth === 0), [rows]);

    const totals = useMemo(() => {
        let spend = 0;
        let results = 0;
        let impressions = 0;
        let reach = 0;
        let clicks = 0;
        let dailyActive = 0;
        let inherited = 0;
        for (const row of topRows) {
            const insights = row.ad.insights || {};
            spend += Number(insights.spend) || 0;
            impressions += Number(insights.impressions) || 0;
            reach += Number(insights.reach) || 0;
            clicks += Number(insights.clicks) || 0;
            results += row.result.value || 0;
            if (row.budget.inherited) inherited += 1;
            const active = String(row.ad.effective_status || row.ad.status || '').toUpperCase() === 'ACTIVE';
            if (active && row.budget.daily) dailyActive += row.budget.daily;
        }
        // Alcance somado superestima: a Meta desduplica pessoas dentro de um
        // objeto, nao entre objetos. E a mesma aproximacao que o Gerenciador
        // mostra ao empilhar linhas, e o unico numero derivavel do que temos.
        return {
            spend,
            results,
            impressions,
            reach,
            cpr: results > 0 ? spend / results : null,
            ctr: impressions > 0 ? (clicks / impressions) * 100 : null,
            frequency: reach > 0 ? impressions / reach : null,
            dailyActive,
            inherited
        };
    }, [topRows]);

    const summary = useMemo(() => {
        const counts = { active: 0, paused: 0, other: 0 };
        for (const row of topRows) {
            const st = String(row.ad.effective_status || row.ad.status || '').toUpperCase();
            if (st === 'ACTIVE') counts.active += 1;
            else if (st.includes('PAUSED')) counts.paused += 1;
            else counts.other += 1;
        }
        const parts = [
            t('ads.summary.active', { count: counts.active }),
            t('ads.summary.paused', { count: counts.paused })
        ];
        if (counts.other) parts.push(t('ads.summary.other', { count: counts.other }));
        return parts.join(' · ');
    }, [topRows, t]);

    if (!ads.length) return null;

    const nameLabel = t(`ads.table.${level === 'adset' ? 'adset' : level === 'ad' ? 'ad' : 'campaign'}`);
    const levelNoun = t(`ads.levelSingular.${level}`);
    const treeEnabled = level !== 'ad';

    const totalsBudget = totals.dailyActive > 0
        ? t('ads.totals.dailyActive', { value: formatCurrency(totals.dailyActive, currency) })
        : totals.inherited > 0
            ? t('ads.totals.inherited', { count: totals.inherited })
            : '—';

    return (
        <div className={d.table.container}>
            <div className={d.table.toolbar}>
                <p className={d.table.toolbarTitle}>
                    {t('ads.table.countTitle', { count: ads.length, level: levelNoun })}
                </p>
                <span className="text-xs text-muted-foreground">{summary}</span>
            </div>

            <div className={d.adsTable.scroller}>
            <div className={d.adsTable.head}>
                <div>{nameLabel}</div>
                <div>{t('ads.table.status')}</div>
                <div>{t('ads.table.createdAt')}</div>
                <div className="text-right">{t('ads.table.results')}</div>
                <div className="text-right">{t('ads.table.costPerResult')}</div>
                <div className="text-right">{t('ads.table.spend')}</div>
                <div className="text-right">{t(`ads.metrics.${spec.m5}`)}</div>
                <div className="text-right">{t(`ads.metricsShort.${spec.m6}`)}</div>
                <div className="text-right">{t('ads.table.budget')}</div>
            </div>

            {rows.map((row) => {
                const { ad, depth, result, delivered, cpr, budget, status } = row;
                const numClass = delivered ? d.adsTable.num : d.adsTable.numMuted;
                const thumb = ad.creative?.thumbnail_url || ad.creative?.image_url;
                const expandable = treeEnabled && ad.level !== 'ad' && childrenOf(ad).length > 0;
                const isOpen = expanded.has(ad._id);

                const drill = (e, target) => {
                    e.stopPropagation();
                    if (onDrill) onDrill(ad, target);
                };

                return (
                    // div role=button, nao <button>: chevron e links de drill
                    // dentro da linha sao botoes, e botao aninhado e invalido.
                    <div
                        role="button"
                        tabIndex={0}
                        key={ad._id}
                        className={d.adsTable.row}
                        onClick={() => onOpen(ad)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') onOpen(ad);
                        }}
                    >
                        <div className={d.adsTable.nameCell} style={depth > 0 ? { paddingLeft: depth * 26 } : undefined}>
                            {treeEnabled && (
                                expandable ? (
                                    <button
                                        type="button"
                                        className={d.adsTable.chevron}
                                        aria-label={isOpen ? t('ads.cascade.collapse') : t('ads.cascade.expand')}
                                        aria-expanded={isOpen}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggle(ad._id);
                                        }}
                                    >
                                        <Iconify
                                            icon="eva:chevron-right-fill"
                                            width={16}
                                            className={isOpen ? d.adsTable.chevronIconOpen : d.adsTable.chevronIcon}
                                        />
                                    </button>
                                ) : (
                                    <span className={d.adsTable.chevronSpacer} />
                                )
                            )}
                            {ad.level === 'ad' && (
                                thumb ? (
                                    <img src={thumb} alt="" className={d.table.thumb} />
                                ) : (
                                    <div className={d.table.thumbPlaceholder}>
                                        <Iconify icon="eva:image-outline" width={16} />
                                    </div>
                                )
                            )}
                            <div className="min-w-0">
                                <div className={d.adsTable.name}>{ad.name || t('ads.table.noName')}</div>
                                <div className={d.adsTable.metaRow}>
                                    <span className={`${d.adsTable.levelBadge} ${d.tone[LEVEL_BADGE_TONE[ad.level] || 'neutral']}`}>
                                        {t(`ads.table.${ad.level === 'adset' ? 'adset' : ad.level === 'ad' ? 'ad' : 'campaign'}`)}
                                    </span>
                                    {ad.objective && (
                                        <span className={d.adsTable.tag}>
                                            {t(`ads.objective.${ad.objective}`, { defaultValue: ad.objective })}
                                        </span>
                                    )}
                                    {ad.level === 'campaign' && row.counts && row.counts.adsets > 0 && (
                                        <button type="button" className={d.adsTable.drill} onClick={(e) => drill(e, 'adset')}>
                                            {t('ads.structure.adsets', { count: row.counts.adsets })}
                                        </button>
                                    )}
                                    {ad.level === 'campaign' && row.counts && row.counts.ads > 0 && (
                                        <button type="button" className={d.adsTable.drill} onClick={(e) => drill(e, 'ad')}>
                                            {t('ads.structure.ads', { count: row.counts.ads })}
                                        </button>
                                    )}
                                    {row.context && <span className={d.adsTable.context}>{row.context}</span>}
                                    {ad.level === 'adset' && row.counts && row.counts.ads > 0 && (
                                        <button type="button" className={d.adsTable.drill} onClick={(e) => drill(e, 'ad')}>
                                            {t('ads.structure.ads', { count: row.counts.ads })}
                                        </button>
                                    )}
                                    {row.reason && <span className={d.adsTable.reason}>{row.reason}</span>}
                                </div>
                            </div>
                        </div>

                        <div className="min-w-0">
                            <StatusChip tone={status.tone} label={status.label} size="sm" />
                        </div>

                        <div className={d.adsTable.date}>{formatDate(row.created)}</div>

                        <div className={d.adsTable.numCell}>
                            <div className={delivered ? d.adsTable.numStrong : d.adsTable.numMuted}>
                                {result.value !== null ? formatInt(result.value) : '—'}
                            </div>
                            {result.labelKey && (
                                <div className={d.adsTable.numSub}>{resultLabel(result.labelKey, t)}</div>
                            )}
                        </div>

                        <div className={d.adsTable.numCell}>
                            <div className={numClass}>{cpr !== null ? formatCurrency(cpr, currency) : '—'}</div>
                        </div>

                        <div className={d.adsTable.numCell}>
                            <div className={numClass}>
                                {delivered ? formatCurrency(ad.insights?.spend, currency) : '—'}
                            </div>
                        </div>

                        <div className={d.adsTable.numCell}>
                            <div className={numClass}>{delivered ? metricValue(spec.m5, ad, t) : '—'}</div>
                        </div>

                        <div className={d.adsTable.numCell}>
                            <div className={numClass}>{delivered ? metricValue(spec.m6, ad, t) : '—'}</div>
                        </div>

                        <div className={d.adsTable.numCell}>
                            <div className={budget.inherited ? d.adsTable.numMuted : d.adsTable.num}>
                                {budget.value}
                            </div>
                            {budget.sub && <div className={d.adsTable.numSub}>{budget.sub}</div>}
                        </div>
                    </div>
                );
            })}

            <div className={d.adsTable.totals}>
                <div className={d.adsTable.totalLabel}>{t('ads.totals.label')}</div>
                <div />
                <div />
                <div className={d.adsTable.totalNum}>{formatInt(totals.results)}</div>
                <div className={d.adsTable.totalNum}>
                    {totals.cpr !== null ? formatCurrency(totals.cpr, currency) : '—'}
                </div>
                <div className={d.adsTable.totalNum}>{formatCurrency(totals.spend, currency)}</div>
                <div className={d.adsTable.totalNum}>
                    {formatInt(spec.m5 === 'reach' ? totals.reach : totals.impressions)}
                </div>
                <div className={d.adsTable.totalNum}>
                    {spec.m6 === 'ctr'
                        ? (totals.ctr !== null ? formatPercent(totals.ctr) : '—')
                        : (totals.frequency !== null ? formatDecimal(totals.frequency, 1) : '—')}
                </div>
                <div className={d.adsTable.totalNote}>{totalsBudget}</div>
            </div>
            </div>
        </div>
    );
}
